import { Component, OnInit, OnDestroy, Inject, Renderer2, ViewChild, TemplateRef, inject } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ShoppingCartDto, ShoppingCartItemDto, ShoppingCartService } from '@proxy/shopping-carts';
import { SommelierService } from '@proxy/sommeliers';
import { WineService } from '@proxy/wines';
import { StorageService } from '../../shared/services/storage.service';
import { MetroToastService } from '../../shared/services/metro-toast.service';
import { SelectedSommelierService } from '../../shared/services/selected-sommelier.service';
import { APP_ICONS } from '../../shared/icons/app-icons';
import { Location } from '@angular/common';

@Component({
  selector: 'app-assisted-cart',
  templateUrl: './assisted-cart.component.html',
  standalone: false
})
export class AssistedCartComponent implements OnInit, OnDestroy {
  userId: string;
  clientName: string = '';
  sommelierSlug: string;
  cartItems: ShoppingCartItemDto[] = [];
  isLoading = true;
  isUpdating: { [wineId: number]: boolean } = {};
  iconBack = APP_ICONS.arrow;
  iconCart = APP_ICONS.cart;
  showCatalogModal = false;

  constructor(
    private route: ActivatedRoute,
    private shoppingCartService: ShoppingCartService,
    private sommelierService: SommelierService,
    private wineService: WineService,
    private storageService: StorageService,
    private toasterService: MetroToastService,
    private selectedSommelierService: SelectedSommelierService,
    private location: Location,
    private renderer: Renderer2,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private localizationService: LocalizationService
  ) {
    this.userId = this.route.snapshot.paramMap.get('userId') || '';
    this.sommelierSlug = this.selectedSommelierService.currentSlugValue || '';
  }

  ngOnInit(): void {
    this.loadCart();
  }

  ngOnDestroy(): void {
  }

  loadCart(): void {
    if (!this.userId) return;
    this.isLoading = true;

    // Fetch client name first or in parallel
    this.sommelierService.getMyClients({}).subscribe(clients => {
      const client = clients.find(c => c.userId === this.userId);
      if (client) {
        this.clientName = `${client.clientName} ${client.clientSurname}`;
      }
    });

    this.shoppingCartService.getCart(this.userId, this.sommelierSlug).subscribe({
      next: (cart) => {
        this.cartItems = cart.items || [];
        this.loadThumbnails();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading client cart', err);
        this.isLoading = false;
      }
    });
  }



  loadThumbnails(): void {
    this.cartItems.forEach(item => {
      if (item.wineFileId) {
        this.storageService.getImage(item.wineFileId, 100, 100).subscribe(blob => {
          (item as any).imageUrl = URL.createObjectURL(blob);
        });
      }
    });
  }

  updateQuantity(wineId: number, event: any): void {
    const newQuantity = event.quantity;
    if (!newQuantity || newQuantity < 1) return;
    
    this.isUpdating[wineId] = true;
    this.shoppingCartService.updateItemQuantity(this.userId, wineId, newQuantity).subscribe({
      next: (cart) => {
        const item = this.cartItems.find(i => i.wineId === wineId);
        if (item) item.quantity = newQuantity;
        this.isUpdating[wineId] = false;
      },
      error: (err) => {
        this.isUpdating[wineId] = false;
        this.toasterService.error('::ErrorUpdatingQuantity');
      }
    });
  }

  removeItem(wineId: number): void {
    this.isUpdating[wineId] = true;
    this.shoppingCartService.removeItemHard(this.userId, wineId).subscribe({
      next: () => {
        this.cartItems = this.cartItems.filter(i => i.wineId !== wineId);
        this.isUpdating[wineId] = false;
        this.toasterService.success('::ItemRemoved');
      },
      error: (err) => {
        this.isUpdating[wineId] = false;
        this.toasterService.error('::ErrorRemovingItem');
      }
    });
  }

  get total(): number {
    return this.cartItems.reduce((acc, item) => acc + (item.unitPrice * item.quantity), 0);
  }

  get subtotal(): number {
    return this.total / 1.21;
  }

  get tax(): number {
    return this.total - this.subtotal;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat(this.localizationService.currentLang || 'es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  }

  goBack(): void {
    this.location.back();
  }

  goToCatalog(): void {
    this.showCatalogModal = true;
    this.renderer.addClass(this.document.body, 'modal-open');
  }

  closeCatalogModal(): void {
    this.showCatalogModal = false;
    this.renderer.removeClass(this.document.body, 'modal-open');
    this.loadCart(); // Reload to see new items
  }
}
